/**
 * Jalali dates (design-spec.md §7): `۱۴۰۵/۰۵/۱۹ · ۱۴:۳۲`.
 *
 * The Persian calendar and Persian digits both ship in ICU, so this delegates
 * to Intl rather than carrying a conversion table. Android's WebView bundles a
 * full ICU, so the Capacitor build behaves the same as the browser.
 *
 * The API sends ISO-8601 with an offset (backend-routes.md §1); rendering in
 * Tehran time is the client's job.
 */

const TIME_ZONE = 'Asia/Tehran'
const LOCALE = 'fa-IR-u-ca-persian-nu-arabext'

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const timeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const longDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

const dayMonthFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  month: 'long',
  day: 'numeric',
})

function parse(value: string | Date): Date {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`invalid timestamp: ${String(value)}`)
  }
  return date
}

/** `۱۴۰۵/۰۵/۱۹` */
export function formatJalaliDate(value: string | Date): string {
  return dateFormatter.format(parse(value))
}

/** `۱۴:۳۲` */
export function formatTime(value: string | Date): string {
  return timeFormatter.format(parse(value))
}

/** `۱۴۰۵/۰۵/۱۹ · ۱۴:۳۲` — the separator the design uses. */
export function formatJalaliDateTime(value: string | Date): string {
  const date = parse(value)
  return `${dateFormatter.format(date)} · ${timeFormatter.format(date)}`
}

/** `۱۹ مرداد` — a day within a span short enough that the year goes without saying. */
export function formatJalaliDayMonth(value: string | Date): string {
  return dayMonthFormatter.format(parse(value))
}

/** `۱۹ مرداد ۱۴۰۵` — for transaction day headers. */
export function formatJalaliLong(value: string | Date): string {
  return longDateFormatter.format(parse(value))
}

/**
 * The day header over a transaction group: `امروز · ۱۹ مرداد`,
 * `دیروز · ۱۸ مرداد`, and `۱۷ مرداد ۱۴۰۵` for anything older.
 *
 * Compared on the Jalali day key rather than by subtracting timestamps, so a
 * DST shift or a device clock in another zone cannot move the boundary.
 */
export function formatJalaliDayHeader(value: string | Date, now: Date = new Date()): string {
  const key = jalaliDayKey(value)
  const yesterday = new Date(now.getTime() - 86_400_000)
  if (key === jalaliDayKey(now)) return `امروز · ${dayMonthFormatter.format(parse(value))}`
  if (key === jalaliDayKey(yesterday)) return `دیروز · ${dayMonthFormatter.format(parse(value))}`
  return formatJalaliLong(value)
}

/** Stable key for grouping a transaction list by day. */
export function jalaliDayKey(value: string | Date): string {
  return dateFormatter.format(parse(value))
}

/** Jalali month names, index 0 = فروردین. */
export const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const

const yearFormatter = new Intl.DateTimeFormat('en-u-ca-persian', {
  timeZone: TIME_ZONE,
  year: 'numeric',
})

/** The current Jalali year as a number, for building birth-date ranges. */
export function currentJalaliYear(now: Date = new Date()): number {
  return Number.parseInt(yearFormatter.format(now).replace(/\D/g, ''), 10)
}

/**
 * A Jalali date the user is still typing, held as three separate parts
 * because the form collects year, month and day in separate controls.
 */
export interface JalaliDateParts {
  year: string
  month: string
  day: string
}

export const EMPTY_DATE: JalaliDateParts = { year: '', month: '', day: '' }

/** `{year:'1372',month:'5',day:'15'}` → `1372-05-15`, or null when incomplete. */
export function toJalaliWire(parts: JalaliDateParts): string | null {
  if (!parts.year || !parts.month || !parts.day) return null
  return `${parts.year}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}`
}
